// Customizable Area Start
import React, { useEffect } from "react";
import { createStyles, Theme } from "@material-ui/core/styles";
import {
  Container,
  Typography,
  IconButton,
  withStyles,
  Box,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FileDropzone from "./FileDropzone.web";
import { File } from "./types";
import { NewPostCreationSteps } from "./enums";
import { InstagramIcon, TikTokIcon } from "./assets"
import CustomButton from "../../../components/src/CustomButton.web";

export type Props = {
  classes: any;
  errorMessage?: string
  closeModal: () => void;
  files: File[];
  setFiles: (files: File[]) => void;
  openLibrary: () => void;
  setStep: (step: any) => void;
  postType?: string;
  libraryType?: any;
  loading?: boolean;
};

const styles = (theme: Theme) =>
  createStyles({
    modal: {
      position: "relative",
      display: "flex",
      padding: "35px 0px",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: "22px",
      flexShrink: 0,
      background: "#FFF",
      "& *": {
        fontFamily: "Inter, 'sans-serif'"
      }
    },

    closeButton: {
      position: "absolute",
      top: "20px",
      right: "11px",
    },

    modalTitle: {
      alignSelf: "flex-start",
      paddingLeft: "18px",
      fontSize: "32px",
      fontWeight: 700,
      lineHeight: "32px",
      fontFamily: "Inter",
      letterSpacing: "-0.12px",
      [theme.breakpoints.down(650)]: {
        fontSize: '24px',
        paddingLeft: "0"
      },
    },

    libraryButton: {
      width: "132px",
      height: "44px",
      alignSelf: "flex-end",
      fontSize: "10px",
      padding: "8px 15px"
    },
    buttonIcon: {
      height: "16.5px",
      width: "16.5px"
    },
    errorMessage: {
      fontSize: "12px",
      color: "#ff0000"
    },
    tikTokIconWrapper: {
      border: "2px solid #000",
      borderRadius: "5px",
      "& img": {
        height: "13px",
        width: "13px"
      }
    },
    bottomButtons: {
      display: "flex",
      justifyContent: "flex-end",
      width: "100%",
      gridGap: "10px",
      [theme.breakpoints.down(650)]: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        alignItems: "center",
        justifyContent: "center",
        width: "unset"
      },
    },
    loaderOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(255,255,255,0.85)",
      zIndex: 10,
      gap: "16px",
    },
    spinner: {
      width: "48px",
      height: "48px",
      border: "4px solid #e0e0e0",
      borderTop: "4px solid #FFC629",
      borderRadius: "50%",
      animation: "$spin 0.8s linear infinite",
    },
    "@keyframes spin": {
      "0%": { transform: "rotate(0deg)" },
      "100%": { transform: "rotate(360deg)" },
    },
    loaderText: {
      fontSize: "15px",
      fontWeight: 600,
      color: "#333",
      fontFamily: "Inter",
    },
  });

export const FileDropzoneModal: React.FC<Props> = ({
  classes,
  postType,
  libraryType,
  errorMessage,
  closeModal,
  files,
  setStep,
  setFiles,
  openLibrary,
  loading,
}) => {

  useEffect(() => {
    setFiles([])
  }, [])

  return (
    <Container
      id="selectFileContainer"
      maxWidth={false}
      className={classes.modal}
      onClick={(e) => e.stopPropagation()}
    >
      {loading && (
        <div className={classes.loaderOverlay}>
          <div className={classes.spinner} />
          <Typography className={classes.loaderText}>Uploading video...</Typography>
        </div>
      )}
      <IconButton
        id="closeModalButton"
        className={classes.closeButton}
        onClick={closeModal}
      >
        <CloseIcon />
      </IconButton>
      <Typography className={classes.modalTitle}>{postType === "post" ? "New Post" : libraryType === "SOCIAL_SCROLL" ? "New Everybrand" : "New Brandly"}</Typography>
      <FileDropzone postType={postType} files={files} setFiles={setFiles} />
      <Box hidden={!errorMessage}>
        <Typography data-test-id="media-error" className={classes.errorMessage}>
          {errorMessage}
        </Typography>
      </Box>
      <Box className={classes.bottomButtons}>
      <CustomButton
        id="openInstagramLibrary"
        className={classes.libraryButton}
        btnType="secondary-outlined"
        hidden={postType !== "post"}
        startIcon={<img className={classes.buttonIcon} src={InstagramIcon} alt="Instagram" />}
        onClick={() => setStep(NewPostCreationSteps.INSTAGRAM_LIBRARY)}
      >
        Instagram
      </CustomButton>
      <CustomButton
        id="openTikTokLibrary"
        className={classes.libraryButton}
        btnType="secondary-outlined"
        hidden={postType !== "post"}
        startIcon={<div className={classes.tikTokIconWrapper}><img src={TikTokIcon} alt="TikTok" /></div>}
        onClick={() => setStep(NewPostCreationSteps.TIK_TOK_LIBRARY)}
      >
        Tik Tok
      </CustomButton>
      <CustomButton
        id="openLibraryButton"
        className={classes.libraryButton}
        btnType="secondary-outlined"
        onClick={openLibrary}
      >
        Everybrand Library
      </CustomButton>
      </Box>
    </Container>
  );
};

export default withStyles(styles)(FileDropzoneModal);
// Customizable Area End
